import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Input, Modal, Radio, Typography } from '@musinsa/pbo-core'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'

// moms-harness:anchor v2
// JIRA: LOGISPRD-13286

/** 상세사유 최소 길이 — 1.0 RemarkPop 검증과 동일. */
export const REASON_DETAIL_MIN_LENGTH = 5

/**
 * 상세사유 최대 길이.
 *
 * BE 는 사유를 감사로그 `MOMS_USER_ACCESS_LOG.remark`(varchar 1000)에 넣고, 엑셀은 GET 이라
 * 사유가 쿼리스트링에 실린다. 한글은 URL 인코딩 시 3배로 불어나므로 넉넉히 잡아도 200 이면 충분하다.
 */
export const REASON_DETAIL_MAX_LENGTH = 200

interface Props {
  open: boolean
  onConfirm: (params: { reasonCode: string; reasonDetail: string }) => void
  onCancel: () => void
}

/**
 * MA18 엑셀 다운로드 사유 모달. (1.0 RemarkPop 동치 — ST02/EST04 와 동일 구성)
 *
 * ### MA02 것을 **복사했다** — import 하지 않는다
 *
 * 내용은 `productManagement/components/ExcelDownloadReasonModal.tsx` 와 같다. 재사용하면 사유 형식이
 * 두 화면에서 갈리지 않는 이점이 있지만, **반대로 MA02 를 고치는 사람이 이 화면도 바뀐다는 것을
 * 모른다.** MA18 은 운영 중인 화면에 사이드이펙트를 만들지 않는 것이 전제라 격리를 택했다.
 *
 * > **그래서 한쪽을 고치면 다른 쪽은 따라오지 않는다.** 사유 입력 규약(코드 + 5자 이상 상세)이
 * > 1.0 RemarkPop 동치라는 점이 두 화면의 공통 근거이므로, 그 규약이 바뀌면 **두 파일을 함께**
 * > 고쳐야 한다.
 *
 * pbo-core `logistics/ExcelDownloadReasonModal` 을 쓰지 않는 이유는 MA02 와 같다 — 그 컴포넌트의
 * `onConfirm` 은 인자가 없어 사유코드/상세사유를 꺼낼 수 없고(입력값 유실), **파일 비밀번호 필드가
 * 필수로 노출**되지만 이 화면은 암호화 zip 계약이 없어(PII 없음) 입력해도 적용되지 않는다(사용자 기만).
 *
 * 사유는 다운로드 요청 파라미터(`detailedReason`)로 실어 BE 가 EXPORT 감사로그에 남긴다.
 */
export function ExcelDownloadReasonModal({ open, onConfirm, onCancel }: Readonly<Props>) {
  const { data: combo, isLoading: isComboLoading } = useGetExportCombo()
  // 콤보는 코드/명 배열이 짝을 이룬다. 명(COMBO_OPT_NM)이 비면 코드로 대체한다(ST02 와 같은 규약).
  const options = useMemo(
    () =>
      (combo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code, idx) => ({
        value: code,
        label: combo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[idx] || code,
      })),
    [combo],
  )

  const [reasonCode, setReasonCode] = useState('')
  const [reasonDetail, setReasonDetail] = useState('')

  // 모달이 언마운트 없이 `open` 만 토글된다 — 비우지 않으면 이전 사유가 남는다.
  useEffect(() => {
    if (!open) return
    setReasonCode('')
    setReasonDetail('')
  }, [open])

  const isValid = Boolean(reasonCode) && reasonDetail.trim().length >= REASON_DETAIL_MIN_LENGTH

  const handleOk = useCallback(() => {
    if (!isValid) return
    onConfirm({ reasonCode, reasonDetail: reasonDetail.trim() })
  }, [isValid, reasonCode, reasonDetail, onConfirm])

  return (
    <Modal open={open} title="다운로드 사유" width={560} okText="확인" cancelText="취소" okDisabled={!isValid} onOk={handleOk} onCancel={onCancel}>
      <div>
        <FieldLabel>사유코드</FieldLabel>
        {/* 콤보를 받아오는 동안은 빈 라디오 대신 로딩을 알린다 (ST02 준용). */}
        {isComboLoading ? (
          <Typography.Text type="secondary">사유코드를 불러오는 중입니다…</Typography.Text>
        ) : (
          <Radio.Group
            value={reasonCode}
            onChange={(e) => setReasonCode(String(e.target.value))}
            style={{ marginTop: 8, display: 'flex', flexWrap: 'nowrap', gap: 16, whiteSpace: 'nowrap' }}
          >
            {options.map((o) => (
              <Radio key={o.value} value={o.value}>
                {o.label}
              </Radio>
            ))}
          </Radio.Group>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <FieldLabel>상세사유</FieldLabel>
        <Input.TextArea
          rows={3}
          placeholder="사유를 입력하세요"
          value={reasonDetail}
          onChange={(e) => setReasonDetail(e.target.value)}
          maxLength={REASON_DETAIL_MAX_LENGTH}
          showCount
          style={{ marginTop: 8 }}
        />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {`${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해주세요.`}
        </Typography.Text>
      </div>
    </Modal>
  )
}

/** 필수 표시(*)가 붙은 필드 라벨 — 사유코드·상세사유 둘 다 필수다. */
function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <Typography.Text>
      {children} <span style={{ color: '#ff4d4f' }}>*</span>
    </Typography.Text>
  )
}

/**
 * 사유 모달 입력 → 감사용 사유 문자열. 1.0 RemarkPop `REMARK`(`{사유코드}_{상세사유}`) 동치다 —
 * BE `resolveReason` 과 같은 형태라 EXPORT 감사로그(remark)에서 사유코드가 유실되지 않는다.
 *
 * MA02 `searchQuery.ts` 의 같은 이름 함수와 **내용이 같지만 복사본이다** (위 KDoc 의 격리 근거).
 */
export const toDetailedReason = ({ reasonCode, reasonDetail }: { reasonCode: string; reasonDetail: string }): string =>
  `${reasonCode}_${reasonDetail}`
